import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChalkboardTeacher, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import SingleSelectDropdown from '../custom-dropdown/SingleSelectDropdown';
import MultiSelectDropdown from '../custom-dropdown/MultiSelectDropdown';
import HierarchicalMultiSelectDropdown from '../custom-dropdown/HierarchicalMultiSelectDropdown';
import InfoTooltip from '../InfoTooltip';
import { getExamTypes } from '../../utils/examApi';
import { getCategoryGroupsByExamType, getCategoriesByCategoryGroup } from '../../utils/hateoasCategoryApi';
import { useSessionSetup } from '../../hooks';

/**
 * SessionSetupForm component for setting up a practice test session
 * @param {Object} props Component props
 * @param {Object} props.sessionSetup Current session setup state
 * @param {Function} props.handleSetupChange Handler for setup input changes
 * @param {Function} props.handleStartSession Handler for starting session
 * @param {Object} props.themeStyles Theme styles object
 * @param {boolean} props.isDarkMode Dark mode state
 * @param {Array} props.systems Available systems
 * @param {Array} props.difficulties Available difficulties
 * @param {Array} props.filterOptions Available filter options
 * @param {Object} props.hierarchicalDisciplines Hierarchical discipline data
 * @param {Array} props.disciplines Flattened disciplines list
 * @param {Object} props.dropdownLabels Labels for dropdown menus
 * @param {string} props.titleError Title validation error if any
 * @param {string} props.questionCountError Question count validation error if any
 * @param {boolean} props.isLoadingQuestionCount Loading state for question count
 * @param {number} props.availableQuestionsCount Number of available questions
 * @param {boolean} props.isSystemDropdownOpen System dropdown open state
 * @param {boolean} props.isDisciplineDropdownOpen Discipline dropdown open state
 * @param {boolean} props.isFilterDropdownOpen Filter dropdown open state
 * @param {boolean} props.isExamTypeDropdownOpen Exam type dropdown open state
 * @param {boolean} props.isDifficultyDropdownOpen Difficulty dropdown open state
 * @param {Function} props.toggleSystemDropdown Toggle system dropdown
 * @param {Function} props.toggleDisciplineDropdown Toggle discipline dropdown
 * @param {Function} props.toggleFilterDropdown Toggle filter dropdown
 * @param {Function} props.toggleExamTypeDropdown Toggle exam type dropdown
 * @param {Function} props.toggleDifficultyDropdown Toggle difficulty dropdown
 * @param {Function} props.handleSystemChange Handler for system selection
 * @param {Function} props.handleDisciplineChange Handler for discipline selection
 * @param {Function} props.handleFilterChange Handler for filter selection
 * @param {Function} props.handleExamTypeChange Handler for exam type selection
 * @param {Function} props.handleDifficultyChange Handler for difficulty selection
 * @param {Function} props.isSystemSelected Checks if a system is selected
 * @param {Function} props.isDisciplineSelected Checks if a discipline is selected
 * @param {Function} props.isDifficultySelected Checks if a difficulty is selected
 * @param {Function} props.getSelectedSystemsText Gets display text for selected systems
 * @param {Function} props.getSelectedDisciplinesText Gets display text for selected disciplines
 * @param {Function} props.getSelectedDifficultiesText Gets display text for selected difficulties
 * @param {Function} props.handleUserFolderChange Handler for user folder selection
 */
const SessionSetupForm = ({
  sessionSetup,
  handleSetupChange,
  handleStartSession,
  themeStyles,
  error,
  isLoading,
  isDarkMode,
  systems = [],
  difficulties = [],
  filterOptions = [],
  hierarchicalDisciplines = {},
  disciplines = [],
  dropdownLabels = {},
  titleError,
  questionCountError,
  isLoadingQuestionCount,
  availableQuestionsCount,
  fetchQuestionCount,
  handleExamTypeChange,
  isSystemDropdownOpen,
  isDisciplineDropdownOpen,
  isFilterDropdownOpen,
  isExamTypeDropdownOpen,
  isDifficultyDropdownOpen,
  toggleSystemDropdown,
  toggleDisciplineDropdown,
  toggleFilterDropdown,
  toggleExamTypeDropdown,
  toggleDifficultyDropdown,
  handleSystemChange,
  handleDisciplineChange,
  handleFilterChange,
  handleDifficultyChange,
  isSystemSelected,
  isDisciplineSelected,
  isDifficultySelected,
  getSelectedSystemsText,
  getSelectedDisciplinesText,
  getSelectedDifficultiesText,
  systemOptions: propSystemOptions = [],
  disciplineOptions: propDisciplineOptions = [],
  examTypes: propExamTypes = [],
  isLoadingExamTypes = true,
  userFolders = [],
  isLoadingFolders = false,
  isUserFolderDropdownOpen,
  toggleUserFolderDropdown,
}) => {
  // Local dropdown open state for exam type
  const [isExamTypeOpen, setIsExamTypeOpen] = useState(false);
  // Local selected exam type state synchronized with sessionSetup.examType
  const [selectedExamType, setSelectedExamType] = useState(null); // store the full exam type object
  const [examTypes, setExamTypes] = useState([]);
  const [loadingExamTypes, setLoadingExamTypes] = useState(true);
  const [loadExamTypesError, setLoadExamTypesError] = useState(null);  const [categoryGroups, setCategoryGroups] = useState([]);
  // Use props instead of local state for system and discipline options
  const systemOptions = propSystemOptions;
  const disciplineOptions = propDisciplineOptions;
  const [loadingCategoryGroups, setLoadingCategoryGroups] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  
  const toggleExamTypeOpen = () => setIsExamTypeOpen(prev => !prev);
    // Add wrapper functions to handle object-based selection
  const wrappedHandleExamTypeChange = (examType, isUserInitiated = false) => {
    // Pass both the name and ID to the parent component
    if (typeof examType === 'object' && examType !== null) {
      const examTypeObj = {
        name: examType.name,
        id: examType.id
      };
      //console.log(`[wrappedHandleExamTypeChange] selected: ${examTypeObj.name}, ID: ${examTypeObj.id}, user initiated: ${isUserInitiated}`);
      handleExamTypeChange(examTypeObj);
    } else {
      // Fallback for string input
      //console.log(`[wrappedHandleExamTypeChange] selected: ${examType} (string only, no ID), user initiated: ${isUserInitiated}`);
      handleExamTypeChange(examType);
    }
      // Only reset other dropdowns when this is a user-initiated change, not during initial setup
    if (isUserInitiated) {
      //console.log('[wrappedHandleExamTypeChange] Resetting other dropdowns due to user-initiated exam type change');
      handleSetupChange({ target: { name: 'system', value: ['ALL'] } });
      handleSetupChange({ target: { name: 'discipline', value: ['ALL'] } });
      handleSetupChange({ target: { name: 'difficulty', value: ['ALL'] } });
      handleSetupChange({ target: { name: 'filterOption', value: 'None' } });
    }
  };

  // Updated wrapper functions to handle object-based selection
  const wrappedIsSystemSelected = (system) => {
    // Handle both string and object inputs
    const systemName = typeof system === 'object' ? system.name : system;
    //console.log(`[wrappedIsSystemSelected] checking: ${systemName}`);
    
    if (systemName === 'ALL') {
      return sessionSetup.system.includes('ALL') || 
             sessionSetup.system.some(s => typeof s === 'object' && s.name === 'ALL');
    }
    
    // Check if this system name is in the selection (as object or string)
    const result = sessionSetup.system.some(s => {
      if (typeof s === 'object') return s.name === systemName;
      return s === systemName;
    });
    
    //console.log(`[wrappedIsSystemSelected] ${systemName} is selected: ${result}`);
    return result;  };
  
  const wrappedIsDisciplineSelected = (discipline) => {
    // Handle both string and object inputs
    const disciplineName = typeof discipline === 'object' ? discipline.name : discipline;
    //(`[wrappedIsDisciplineSelected] checking: ${disciplineName}`);
    
    if (disciplineName === 'ALL') {
      return sessionSetup.discipline.includes('ALL') || 
             sessionSetup.discipline.some(d => typeof d === 'object' && d.name === 'ALL');
    }
    
    // Check if this discipline name is in the selection (as object or string)
    const result = sessionSetup.discipline.some(d => {
      if (typeof d === 'object') return d.name === disciplineName;
      return d === disciplineName;
    });
    
    //console.log(`[wrappedIsDisciplineSelected] ${disciplineName} is selected: ${result}`);
    return result;
  };  const wrappedHandleSystemChange = (eventOrSystem) => {
    //console.log('[wrappedHandleSystemChange] called with:', eventOrSystem);
    
    // Handle both old format (direct system object) and new hierarchical format (event object)
    let system, eventType;
    if (eventOrSystem && typeof eventOrSystem === 'object' && eventOrSystem.type) {
      // New hierarchical format
      eventType = eventOrSystem.type;
      system = eventOrSystem.option;
    } else {
      // Old format - direct system selection
      system = eventOrSystem;
      eventType = 'child-select';
    }
    
    const systemName = typeof system === 'object' ? system.name : system;
    let newSelection = [];

    if (systemName === 'ALL') {
      newSelection = ['ALL'];
    } else {
      // Get the current system selection without 'ALL'
      const currentSelection = sessionSetup.system.filter(s => {
        // Handle both string and object items
        if (typeof s === 'object') return s.name !== 'ALL';
        return s !== 'ALL';
      });
      
      // Check if this system is already selected
      const isSelected = currentSelection.some(s => {
        if (typeof s === 'object') return s.name === systemName;
        return s === systemName;
      });

      // Handle different event types for hierarchical selection
      if (eventType === 'parent-select') {
        // Parent selected - add parent and all children names
        //console.log('[wrappedHandleSystemChange] Parent selected:', system);
        const childNames = eventOrSystem.childNames || [];
        newSelection = [...currentSelection.filter(s => {
          const name = typeof s === 'object' ? s.name : s;
          return name !== systemName && !childNames.includes(name);
        }), systemName, ...childNames];
      } else if (eventType === 'parent-deselect') {
        // Parent deselected - remove parent and all children
       // console.log('[wrappedHandleSystemChange] Parent deselected:', system);
        const childNames = eventOrSystem.childNames || [];
        newSelection = currentSelection.filter(s => {
          const name = typeof s === 'object' ? s.name : s;
          return name !== systemName && !childNames.includes(name);
        });
      } else {
        // Regular child selection
        if (isSelected) {
          // Remove this system
          newSelection = currentSelection.filter(s => {
            if (typeof s === 'object') return s.name !== systemName;
            return s !== systemName;
          });
        } else {
          // Add this system
          newSelection = [...currentSelection, systemName];
        }
      }
      
      if (newSelection.length === 0) {
        newSelection = ['ALL'];
      }
    }

    handleSetupChange({
      target: {
        name: 'system',
        value: newSelection
      }
    });
  };  const wrappedHandleDisciplineChange = (eventOrDiscipline) => {
    //console.log('[wrappedHandleDisciplineChange] called with:', eventOrDiscipline);
    
    // Handle both old format (direct discipline object) and new hierarchical format (event object)
    let discipline, eventType;
    if (eventOrDiscipline && typeof eventOrDiscipline === 'object' && eventOrDiscipline.type) {
      // New hierarchical format
      eventType = eventOrDiscipline.type;
      discipline = eventOrDiscipline.option;
    } else {
      // Old format - direct discipline selection
      discipline = eventOrDiscipline;
      eventType = 'child-select';
    }
    
    const disciplineName = typeof discipline === 'object' ? discipline.name : discipline;
    let newSelection = [];

    if (disciplineName === 'ALL') {
      newSelection = ['ALL'];
    } else {
      // Get the current discipline selection without 'ALL'
      const currentSelection = sessionSetup.discipline.filter(d => {
        // Handle both string and object items
        if (typeof d === 'object') return d.name !== 'ALL';
        return d !== 'ALL';
      });
      
      // Check if this discipline is already selected
      const isSelected = currentSelection.some(d => {
        if (typeof d === 'object') return d.name === disciplineName;
        return d === disciplineName;
      });

      // Handle different event types for hierarchical selection
      if (eventType === 'parent-select') {
        // Parent selected - add parent and all children names
        //console.log('[wrappedHandleDisciplineChange] Parent selected:', discipline);
        const childNames = eventOrDiscipline.childNames || [];
        newSelection = [...currentSelection.filter(d => {
          const name = typeof d === 'object' ? d.name : d;
          return name !== disciplineName && !childNames.includes(name);
        }), disciplineName, ...childNames];
      } else if (eventType === 'parent-deselect') {
        // Parent deselected - remove parent and all children
        //console.log('[wrappedHandleDisciplineChange] Parent deselected:', discipline);
        const childNames = eventOrDiscipline.childNames || [];
        newSelection = currentSelection.filter(d => {
          const name = typeof d === 'object' ? d.name : d;
          return name !== disciplineName && !childNames.includes(name);
        });
      } else {
        // Regular child selection
        if (isSelected) {
          // Remove this discipline
          newSelection = currentSelection.filter(d => {
            if (typeof d === 'object') return d.name !== disciplineName;
            return d !== disciplineName;
          });
        } else {
          // Add this discipline
          newSelection = [...currentSelection, disciplineName];
        }
      }
      
      if (newSelection.length === 0) {
        newSelection = ['ALL'];
      }
    }

    handleSetupChange({
      target: {
        name: 'discipline',
        value: newSelection
      }
    });  };
  
  const wrappedHandleDifficultyChange = (difficulty) => {
    const difficultyName = typeof difficulty === 'object' ? difficulty.name : difficulty;
    let newSelection = [];

    if (difficultyName === 'ALL') {
      newSelection = ['ALL'];
    } else {
      // Get the current difficulty selection without 'ALL'
      const currentSelection = sessionSetup.difficulty.filter(d => {
        // Handle both string and object items
        if (typeof d === 'object') return d.name !== 'ALL';
        return d !== 'ALL';
      });
      
      // Check if this difficulty is already selected
      const isSelected = currentSelection.some(d => {
        if (typeof d === 'object') return d.name === difficultyName;
        return d === difficultyName;
      });

      if (isSelected) {
        // Remove this difficulty
        newSelection = currentSelection.filter(d => {
          if (typeof d === 'object') return d.name !== difficultyName;
          return d !== difficultyName;
        });
      } else {
        // Add this difficulty (always store just the name for rendering compatibility)
        const difficultyToAdd = difficultyName; // Use just the name to avoid React rendering issues
        newSelection = [...currentSelection, difficultyToAdd];
      }
      
      if (newSelection.length === 0) {
        newSelection = ['ALL'];
      }
    }

    handleSetupChange({
      target: {
        name: 'difficulty',
        value: newSelection
      }
    });
  };
  
  // Simplified function since category data comes from props
  const fetchCategoryGroupsAndCategories = async (examTypeObj) => {
    //console.log('[fetchCategoryGroupsAndCategories] called with', examTypeObj);
    if (!examTypeObj) {
      console.warn('[fetchCategoryGroupsAndCategories] No exam type object provided');
      return;
    }
    
    // Note: Category data is now fetched in the container and passed as props
    // This function is kept for compatibility but no longer fetches categories
    // console.log('[fetchCategoryGroupsAndCategories] Using category data from props');
    // console.log('[fetchCategoryGroupsAndCategories] systemOptions:', propSystemOptions);
    // console.log('[fetchCategoryGroupsAndCategories] disciplineOptions:', propDisciplineOptions);
  };
  // Fetch exam types once on mount or when prop changes
  useEffect(() => {
   // console.log('[SessionSetupForm] 🔄 useEffect triggered for exam types');
    //console.log('[SessionSetupForm] 📍 Call stack for useEffect:', new Error().stack.split('\n').slice(0, 8).join('\n'));
    const fetchExamTypes = async () => {
      // Don't fetch if parent is still loading exam types
      if (isLoadingExamTypes) {
        //console.log('[SessionSetupForm] ⏳ Parent is still loading exam types, waiting... (isLoadingExamTypes:', isLoadingExamTypes, ')');
        return;
      }
      
      // Don't fetch if we already have exam types from props
      if (propExamTypes && propExamTypes.length > 0) {
        //console.log('[SessionSetupForm] ✅ Using examTypes from props:', propExamTypes.length, 'types (isLoadingExamTypes:', isLoadingExamTypes, ')');
        setExamTypes(propExamTypes);
        setLoadingExamTypes(false);
        setLoadExamTypesError(null);
        // Set first as default if not already set
        if (!selectedExamType && propExamTypes.length > 0) {
          setSelectedExamType(propExamTypes[0]);
          if (!sessionSetup.examType || !sessionSetup.examTypeId) {
            wrappedHandleExamTypeChange(propExamTypes[0]);
          }
        }
        return;
      }

      //console.log('[SessionSetupForm] 🚀 Props not available, fetching exam types from API... (propExamTypes length:', propExamTypes?.length || 0, ', isLoadingExamTypes:', isLoadingExamTypes, ')');
      //console.log('[SessionSetupForm] 📍 About to call getExamTypes() from utils/examApi');

      setLoadingExamTypes(true);
      //console.log('[SessionSetupForm] 📡 Fetching exam types from API...');
      try {
        const types = await getExamTypes();
        //console.log('[SessionSetupForm] ✅ API returned exam types:', types?.length || 0, 'types');
        
        if (!Array.isArray(types) || types.length === 0) {
          throw new Error('No exam types returned or invalid format');
        }
        
       // console.log('[fetchExamTypes] First exam type:', types[0]);
        
        // Verify the structure of the exam type objects
        if (!types[0].name || !types[0]._links || !types[0]._links.categoryGroups) {
        //  console.warn('[fetchExamTypes] Exam type data structure may be incorrect:', types[0]);
        }
        
        setExamTypes(types);
        setLoadExamTypesError(null);
          // Always set first as default
        setSelectedExamType(types[0]);
        // Update parent component state using our wrapper - only if not already set
        if (!sessionSetup.examType || !sessionSetup.examTypeId) {
          wrappedHandleExamTypeChange(types[0]);
        }
        
       // console.log('[fetchExamTypes] Selected exam type set to:', types[0]);
        
      } catch (error) {
        setLoadExamTypesError('Failed to load exam types.');
        console.error('[fetchExamTypes] error:', error);
      } finally {
        setLoadingExamTypes(false);
      }
    };

    fetchExamTypes();
    // eslint-disable-next-line
  }, [propExamTypes, isLoadingExamTypes]);// Fetch category groups and categories when exam type changes (user selection)
  useEffect(() => {
    if (!selectedExamType) {
      // console.log('[useEffect] selectedExamType not set, skipping fetch');
      return;
    }
    
    // console.log('[useEffect] selectedExamType changed:', selectedExamType);
    
    // Add additional check for required links
    if (!selectedExamType._links || !selectedExamType._links.categoryGroups) {
      console.error('[useEffect] Selected exam type is missing required _links.categoryGroups property:', selectedExamType);
      return;
    }
    
    fetchCategoryGroupsAndCategories(selectedExamType);
    // eslint-disable-next-line
  }, [selectedExamType]);
  // Monitor system and discipline options for debugging
  useEffect(() => {
    //console.log('[useEffect] systemOptions updated:', systemOptions);
    if (systemOptions && systemOptions.length > 0) {
      // console.log('[useEffect] systemOptions mapped:', systemOptions.map(option => ({
      //   id: option.id, // Keep id and name for potential future use or debugging
      //   name: option.name,
      //   children: []
      // })));
    }
  }, [systemOptions]);
  
  useEffect(() => {
    //console.log('[useEffect] disciplineOptions updated:', disciplineOptions);
    if (disciplineOptions && disciplineOptions.length > 0) {
      // console.log('[useEffect] disciplineOptions mapped:', disciplineOptions.map(option => ({
      //   id: option.id, // Keep id and name
      //   name: option.name,
      //   children: []
      // })));
    }
  }, [disciplineOptions]);
  // Monitor dropdown open states for debugging
  useEffect(() => {
   // console.log('[useEffect] isSystemDropdownOpen:', isSystemDropdownOpen);
  }, [isSystemDropdownOpen]);
  
  useEffect(() => {
    //console.log('[useEffect] isDisciplineDropdownOpen:', isDisciplineDropdownOpen);
  }, [isDisciplineDropdownOpen]);
  
  // Monitor selection states of system and discipline
  useEffect(() => {
    if (sessionSetup.system) {
     // console.log('[useEffect] current selected systems:', sessionSetup.system);
    }
  }, [sessionSetup.system]);
  
  useEffect(() => {
    if (sessionSetup.discipline) {
      //console.log('[useEffect] current selected disciplines:', sessionSetup.discipline);
    }
  }, [sessionSetup.discipline]);  // Debug helper function to output dropdown states
  const debugDropdowns = () => {
    //console.group('Dropdown Debug Information');
    //console.log('Session setup:', sessionSetup);
    
    // Check if session setup has the expected structure
    if (!sessionSetup || typeof sessionSetup !== 'object') {
      //console.error('Session setup is not an object:', sessionSetup);
    } else {
      if (!Array.isArray(sessionSetup.system)) {
        //console.error('sessionSetup.system is not an array:', sessionSetup.system);
      }
      if (!Array.isArray(sessionSetup.discipline)) {
        //console.error('sessionSetup.discipline is not an array:', sessionSetup.discipline);
      }
    }
    
    // console.log('System dropdown:');
    // console.log('- Options:', systemOptions);
    // console.log('- Is open:', isSystemDropdownOpen);
    // console.log('- Is loading:', loadingCategoryGroups || loadingCategories);
    // console.log('- Selected systems:', sessionSetup.system);
    
    // console.log('Discipline dropdown:');
    // console.log('- Options:', disciplineOptions);
    // console.log('- Is open:', isDisciplineDropdownOpen);
    // console.log('- Is loading:', loadingCategoryGroups || loadingCategories);
    // console.log('- Selected disciplines:', sessionSetup.discipline);
    // console.groupEnd();
  };  // Debounced fetchQuestionCount to prevent excessive API calls
  useEffect(() => {
    // Only fetch if all required fields and options are present
    // console.log('[SessionSetupForm][useEffect][fetchQuestionCount] triggered', {
    //   examTypeId: sessionSetup.examTypeId,
    //   system: sessionSetup.system,
    //   discipline: sessionSetup.discipline,
    //   difficulty: sessionSetup.difficulty,
    //   filterOption: sessionSetup.filterOption,
    //   userFolders: sessionSetup.userFolders
    // });
    if (
      sessionSetup.examTypeId &&
      systemOptions.length > 0 &&
      disciplineOptions.length > 0
    ) {
      // Clear any existing timeout
      const timeoutId = setTimeout(() => {
        // console.log('[SessionSetupForm][useEffect][fetchQuestionCount] calling fetchQuestionCount()');
        fetchQuestionCount();
      }, 300); // 300ms debounce delay

      // Cleanup function to clear timeout on dependency change
      return () => clearTimeout(timeoutId);
    }
  }, [
    sessionSetup.examTypeId,
    sessionSetup.system,
    sessionSetup.discipline,
    sessionSetup.difficulty,
    sessionSetup.filterOption,
    sessionSetup.userFolders, // FIX: use userFolders, not userFolderIds
    fetchQuestionCount // Include fetchQuestionCount in dependencies to satisfy eslint
  ]);
  // Call debug function on render
  useEffect(() => {
    debugDropdowns();
  }, [
    systemOptions, 
    disciplineOptions, 
    isSystemDropdownOpen, 
    isDisciplineDropdownOpen, 
    loadingCategoryGroups, 
    loadingCategories, 
    sessionSetup.system, 
    sessionSetup.discipline
  ]);

  // Merge defaults with provided labels
  const labels = {
    examType: 'exam type',
    discipline: 'discipline',
    system: 'system',
    filter: 'filter',
    difficulty: 'difficulty',
    ...dropdownLabels
  };

  // Normalize folder data for the dropdown, which likely expects a 'label' property for display.
  // This handles potential case mismatches from the API (e.g., 'Name' vs 'name').
  // console.log('[SessionSetupForm][UserFolder] userFolders prop:', userFolders);
  // console.log('[SessionSetupForm][UserFolder] userFolders length:', userFolders?.length);
  // console.log('[SessionSetupForm][UserFolder] isLoadingFolders prop:', isLoadingFolders);
  const folderOptions = userFolders.map(folder => ({
    id: folder.id || folder.Id,
    name: folder.name || folder.Name,
    label: folder.name || folder.Name,
  }));
  // console.log('[SessionSetupForm][UserFolder] folderOptions:', folderOptions);
  // console.log('[SessionSetupForm][UserFolder] userFolderIds:', sessionSetup.userFolders);
  // console.log('[SessionSetupForm][UserFolder] isLoadingFolders:', isLoadingFolders);

  // Helper to get folder names from selected IDs
  const getSelectedFolderNames = () => {
    if (!Array.isArray(sessionSetup.userFolders) || sessionSetup.userFolders.length === 0) return 'None';
    if (sessionSetup.userFolders.length === 1 && (sessionSetup.userFolders[0] === null || sessionSetup.userFolders[0] === 'None')) return 'None';
    return sessionSetup.userFolders
      .map(id => {
        const found = folderOptions.find(f => f.id === id);
        return found ? found.name : id;
      })
      .join(', ');
  };

  // Add console log to track component rendering and props
  // console.log('[SessionSetupForm] Rendering with props:', {
  //   filterOptions,
  //   sessionSetup,
  //   isFilterDropdownOpen
  // }); 
  return (
    <>
      <h2 style={{ 
        color: themeStyles.text.primary, 
        fontSize: '1.125rem', 
        marginBottom: '15px',
        textAlign: 'center',           
      }}>
        <FontAwesomeIcon icon={faChalkboardTeacher} style={{ marginRight: '7.5px' }} />
        Create Practice Session
      </h2>
      
      <div className="form-group">
        <label 
          htmlFor="title" 
          style={{ 
            color: themeStyles.text.primary,
            display: 'block',
            marginBottom: '6px',
            fontWeight: '500'
          }}
        >
          Session Title: <span style={{ color: 'red' }}>*</span>
        </label>
        <input
          type="text"
          id="title"
          name="title"
          value={sessionSetup.title}
          onChange={handleSetupChange}
          placeholder="Enter a title for your practice session (required)"
          maxLength={50}
          style={{
            background: themeStyles.input.background,
            color: themeStyles.input.color,
            border: !sessionSetup.title || sessionSetup.title.trim() === '' ? '1px solid red' : themeStyles.input.border,
            borderRadius: '5px',
            padding: '7.5px 9px',
            width: '50%',
            fontSize: '12px'
          }}
          required
        />
        {(!sessionSetup.title || sessionSetup.title.trim() === '') && (
          <div className="validation-message" style={{ color: 'red', fontSize: '12px', marginTop: '3px' }}>
            Session title is required
          </div>
        )}
        
        {titleError && (
          <div className="validation-message" style={{ color: themeStyles.error.color }}>
            {titleError}
          </div>
        )}
      </div>
      
      <div className="flex-row">
        <div className="form-group">
          <label 
            htmlFor="examType" 
            style={{ 
              color: themeStyles.text.primary,
              display: 'block',
              marginBottom: '6px',
              fontWeight: '500'
            }}
          >
            Exam Type:                
          </label>                
          
          <SingleSelectDropdown
           isOpen={isExamTypeOpen}
           toggleDropdown={toggleExamTypeOpen}
           selectedOption={
             loadingExamTypes ? 'Loading...' :
             loadExamTypesError ? 'Error' :
             (selectedExamType ? selectedExamType.name : '')
           }
            options={loadingExamTypes || loadExamTypesError ? [] : examTypes.map(type => type.name)}           onSelect={option => {
             const found = examTypes.find(e => e.name === option);
             //console.log('[SingleSelectDropdown onSelect] found:', found);
             
             if (found) {
               // Set selected exam type first
               setSelectedExamType(found);
               // Then update the parent component's state using our wrapper
               wrappedHandleExamTypeChange(found, true); // Pass the full found object, mark as user-initiated
               // Note: SingleSelectDropdown component automatically closes itself, no need to manually close
             } else {
               //console.error(`[SingleSelectDropdown onSelect] Could not find exam type with name: ${option}`);
             }
           }}
            themeStyles={themeStyles}
            isDarkMode={isDarkMode}
            label="Select exam type"
            disabled={loadingExamTypes || !!loadExamTypesError}
            uiScale={0.75}
          />
          {loadingExamTypes && <p style={{color: themeStyles.text.secondary}}>Loading exam types...</p>}
          {loadExamTypesError && !loadingExamTypes && (
             <div className="validation-message" style={{ color: themeStyles.error.color }}>
              {loadExamTypesError}
             </div>
           )}
        </div>
        
        <div className="form-group">
          <label 
            htmlFor="questionCount" 
            style={{ 
              color: themeStyles.text.primary,
              display: 'block',
              marginBottom: '6px',
              fontWeight: '500'
            }}
          >
            Number of Questions:
          </label>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <input
              type="number"
              id="questionCount"
              name="questionCount"
              value={sessionSetup.questionCount}
              onChange={e => {
                let value = parseInt(e.target.value, 10);
                let max = 50; // Cap at 50 questions
                if (isNaN(value) || value < 1) value = '';
                if (value > max) value = max;
                handleSetupChange({
                  target: {
                    name: 'questionCount',
                    value: value
                  }
                });
              }}
              min="1"
              max="50"
              className="short-input"
              style={{
                background: themeStyles.input.background,
                color: themeStyles.input.color,
                border: themeStyles.input.border,
                borderRadius: '5px',
                padding: '7.5px 9px',
                fontSize: '12px',
                width: '60px'
              }}
            />
            <span style={{ 
              marginLeft: '6px', 
              color: themeStyles.text.secondary,
              fontSize: '12px',
              display: 'flex',
              alignItems: 'center'
            }}>
              {isLoadingQuestionCount ? (
                <span>Loading...</span>
              ) : (
                <span>/{availableQuestionsCount}</span>
              )}
            </span>
          </div>
          {questionCountError && (
            <div className="validation-message" style={{ color: themeStyles.error.color }}>
              {questionCountError}
            </div>
          )}
        </div>
      </div>
      
      <div className="flex-row">
        <div className="form-group">          <label 
            htmlFor="system" 
            style={{ 
              color: themeStyles.text.primary,
              display: 'block',
              marginBottom: '6px',
              fontWeight: '500'
            }}
          >
            System:
            <InfoTooltip message={'Select multiple systems or "ALL" for all systems'} />
          </label>
          <HierarchicalMultiSelectDropdown
            isOpen={isSystemDropdownOpen}
            toggleDropdown={toggleSystemDropdown}
            displayText={getSelectedSystemsText()}
            options={loadingCategoryGroups || loadingCategories ? [] : systemOptions}
            isSelected={wrappedIsSystemSelected}
            onSelect={wrappedHandleSystemChange}
            themeStyles={themeStyles}
            isDarkMode={isDarkMode}
            label="Select systems"
            uiScale={0.75}
          />
        </div>
        
        <div className="form-group">                  <label 
            htmlFor="discipline" 
            style={{ 
              color: themeStyles.text.primary,
              display: 'block',
              marginBottom: '6px',
              fontWeight: '500'
            }}
          >
            {labels.discipline}:
            <InfoTooltip message={'Select multiple disciplines or "ALL" for all disciplines'} />
          </label>
          <HierarchicalMultiSelectDropdown
            isOpen={isDisciplineDropdownOpen}
            toggleDropdown={toggleDisciplineDropdown}
            displayText={getSelectedDisciplinesText()}
            options={loadingCategoryGroups || loadingCategories ? [] : disciplineOptions}
            isSelected={wrappedIsDisciplineSelected}
            onSelect={wrappedHandleDisciplineChange}
            themeStyles={themeStyles}
            isDarkMode={isDarkMode}
            label={`Select ${labels.discipline.toLowerCase()}`}
            uiScale={0.75}
          />
        </div>
      </div>
      
      <div className="flex-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label 
            htmlFor="filterOption" 
            style={{
              color: themeStyles.text.primary,
              display: 'block',
              marginBottom: '6px',
              fontWeight: '500'
            }}
          >
            Question Filter:
          </label>
          <SingleSelectDropdown
            isOpen={isFilterDropdownOpen}
            toggleDropdown={toggleFilterDropdown}
            selectedOption={sessionSetup.filterOption}
            options={[
              "None",
              "Questions I have never seen",
              "Questions I have answered incorrectly",
              "Questions I have answered correctly",
              "Questions I have seen before"
            ]} // Default options if filterOptions prop is not provided
            onSelect={(filter) => {
              //console.log('[SessionSetupForm] Filter selected:', filter);
              handleFilterChange(filter);
            }}
            themeStyles={themeStyles}
            isDarkMode={isDarkMode}
            label="Select a filter option"
            uiScale={0.75}
          />
        </div>
        
        <div className="form-group" style={{ flex: 1 }}>
          <label
            htmlFor="difficulty"
            style={{
              color: themeStyles.text.primary,
              display: 'block',
              marginBottom: '6px',
              fontWeight: '500'
            }}
          >
            Difficulty:
            <InfoTooltip message={'Select multiple difficulties or "ALL" for all difficulties'} />
          </label>            <MultiSelectDropdown
            isOpen={isDifficultyDropdownOpen}
            toggleDropdown={toggleDifficultyDropdown}
            displayText={getSelectedDifficultiesText()}
            options={difficulties}
            isSelected={isDifficultySelected}
            onSelect={wrappedHandleDifficultyChange}
            themeStyles={themeStyles}
            isDarkMode={isDarkMode}
            label="Select difficulties"
            uiScale={0.75}
          />
        </div>
      </div>          <div className="flex-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label
            htmlFor="userFolderDropdown"
            style={{
              color: themeStyles.text.primary,
              display: 'block',
              marginBottom: '6px',
              fontWeight: '500'
            }}
          >
            User Folder:
          </label>
          <MultiSelectDropdown
            isOpen={isUserFolderDropdownOpen}
            toggleDropdown={toggleUserFolderDropdown}
            displayText={
              isLoadingFolders ? 'Loading...' : getSelectedFolderNames()
            }
            options={folderOptions.length === 0 ? [{ id: null, name: 'None', label: 'None' }] : folderOptions}
            isSelected={option => Array.isArray(sessionSetup.userFolders) && sessionSetup.userFolders.includes(option.id)}
            onSelect={option => {
              let newSelection = Array.isArray(sessionSetup.userFolders) ? [...sessionSetup.userFolders] : [];
              const isSelected = newSelection.includes(option.id);
              if (option.id === null || option.name === 'None') {
                newSelection = [null];
              } else {
                newSelection = newSelection.filter(f => f !== null && f !== 'None');
                if (isSelected) {
                  newSelection = newSelection.filter(f => f !== option.id);
                } else {
                  newSelection.push(option.id);
                }
                if (newSelection.length === 0) newSelection = [null];
              }
              handleSetupChange({ target: { name: 'userFolders', value: newSelection } });
            }}
            themeStyles={themeStyles}
            isDarkMode={isDarkMode}
            label="Select user folders"
            uiScale={0.75}
          />
        </div>
      </div>
      
      {error && (
        <div style={{
          color: themeStyles?.error?.color || '#ef4444',
          marginBottom: '9px',
          textAlign: 'center'
        }}>{error}</div>
      )}

      {/* Show message when no questions are available */}
      {!isLoadingQuestionCount && availableQuestionsCount === 0 && (
        <div style={{
          color: themeStyles?.error?.color || '#ef4444',
          marginBottom: '9px',
          textAlign: 'center',
          fontWeight: '500',
          backgroundColor: isDarkMode ? 'rgba(239, 68, 68, 0.1)' : 'rgba(239, 68, 68, 0.05)',
          padding: '6px 9px',
          borderRadius: '6px',
          border: `1px solid ${themeStyles?.error?.color || '#ef4444'}20`
        }}>
          No questions available for the selected criteria. Please adjust your filters or selection.
        </div>
      )}

      <button 
        onClick={(e) => {
          //console.log('Start Session button clicked');
          // Delegate validation and start logic to parent handler so UI shows inline errors
          if (handleStartSession) {
            handleStartSession(e);
          } else {
            //console.error('handleStartSession prop is not defined');
          }
        }}
        disabled={isLoading || isLoadingQuestionCount || availableQuestionsCount === 0}
        style={{
          background: (isLoading || isLoadingQuestionCount || availableQuestionsCount === 0) 
            ? (isDarkMode ? '#4a4a4a' : '#e5e5e5') 
            : themeStyles.button.primary.background,
          color: (isLoading || isLoadingQuestionCount || availableQuestionsCount === 0)
            ? (isDarkMode ? '#999' : '#666')
            : themeStyles.button.primary.color,
          border: themeStyles.button.primary.border,
          borderRadius: themeStyles.button.primary.borderRadius,
          padding: '9px 15px',
          cursor: (isLoading || isLoadingQuestionCount || availableQuestionsCount === 0) 
            ? 'not-allowed' 
            : themeStyles.button.primary.cursor,
          width: '100%',
          maxWidth: '300px',
          marginTop: '7.5px',
          alignSelf: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '7.5px',
          fontWeight: '600',
          fontSize: '12px',
          transition: 'all 0.3s ease',
          boxShadow: (isLoading || isLoadingQuestionCount || availableQuestionsCount === 0) 
            ? 'none' 
            : '0 4px 6px rgba(0,0,0,0.1)',
          opacity: (isLoading || isLoadingQuestionCount || availableQuestionsCount === 0) ? 0.6 : 1
        }}
      >        
        {isLoading ? 'Starting...' : 'Start Session'} <FontAwesomeIcon icon={faArrowRight} />
      </button>
    </>
  );
};

export default SessionSetupForm;

// If you use useSessionSetup in this file, update its usage like this:
// const sessionSetupState = useSessionSetup(initialSetup, systemOptions, disciplineOptions);
